// Sprint C PR (at): React-side of the `tizok login` OAuth-style flow.
// V1.0 BLOCKER per V1.0_PLAN task #49.
//
// Flow (per V1_INTERFACES.md §1.18 and CLI_DESIGN_NOTES.md `tizok login`):
//
//   1. User runs `tizok login` in the terminal.
//   2. CLI starts a localhost listener + opens the browser to
//      `https://<host>/cli-auth?callback=<URL>&nonce=<N>`.
//   3. Backend gates `/cli-auth` behind a web session — signed-out users
//      land in the operator console's LoginForm (this happens above us
//      via AuthGate; we render only when the operator is signed in).
//   4. We show the operator their identity + an Authorize button.
//   5. Clicking Authorize POSTs `nonce` to `/cli-auth/complete` as
//      form-encoded data — matches the existing axum handler.
//   6. Backend mints the biscuit, POSTs it to the CLI's localhost
//      callback, then renders success/failure HTML. We replace that with
//      a richer in-React success / failure surface here so the operator
//      console feel is preserved end-to-end.

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, fetchMe } from '../lib/api';

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

function readQueryParam(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

export function CliAuthPage() {
  const callback = readQueryParam('callback');
  const nonce = readQueryParam('nonce');
  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: false,
  });
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // Set the page title — useful when the operator has multiple tabs
  // open during the CLI handshake.
  useEffect(() => {
    document.title = 'Authorize Tizok CLI';
  }, []);

  if (!callback || nonce === null) {
    return (
      <CliAuthFrame title="Bad CLI request">
        <p role="alert">
          This page is part of the <code>tizok login</code> flow. It was
          opened without the required <code>callback</code> and{' '}
          <code>nonce</code> query parameters. Re-run{' '}
          <code>tizok login</code> in your terminal.
        </p>
      </CliAuthFrame>
    );
  }

  if (isLoading) {
    return (
      <CliAuthFrame title="Authorize Tizok CLI">
        <p role="status">Checking session…</p>
      </CliAuthFrame>
    );
  }

  if (status.kind === 'success') {
    return (
      <CliAuthFrame title="Login successful">
        <p role="status" aria-live="polite">
          Device authorized.{' '}
          The CLI on your machine is now authenticated. You can close this
          window — your terminal will pick up the token automatically.
        </p>
        <p className="cli-auth-hint">
          If the CLI doesn't continue, run <code>tizok login</code> again.
        </p>
      </CliAuthFrame>
    );
  }

  async function handleAuthorize(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // The nonce is delivered by the CLI via the URL (see flow above) and
    // relayed straight back to the terminal — the operator never types or
    // copies it. Past the guard above it is a non-null string.
    const trimmedToken = (nonce ?? '').trim();
    if (!trimmedToken) {
      setStatus({
        kind: 'error',
        message:
          'This authorization request has expired. Re-run `tizok login` in your terminal.',
      });
      return;
    }
    setStatus({ kind: 'submitting' });

    const formBody = new URLSearchParams();
    formBody.append('nonce', trimmedToken);

    try {
      const res = await apiFetch('/cli-auth/complete', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (res.ok) {
        // Backend returned 2xx — biscuit mint + callback POST succeeded.
        setStatus({ kind: 'success' });
        return;
      }

      // 4xx: surface the error code so the operator knows what to do.
      let detail = '';
      try {
        const body = await res.json();
        detail = body?.error ?? '';
      } catch {
        // ignored — fall through to generic message
      }
      const message =
        detail === 'BAD_NONCE' || detail === 'NONCE_EXPIRED'
          ? 'This authorization request has expired. Re-run `tizok login` in your terminal.'
          : detail === 'INTERNAL'
            ? "Something went wrong on our end. Re-run `tizok login` and we'll try again."
            : "Couldn't complete the login. Re-run `tizok login` in your terminal.";

      setStatus({ kind: 'error', message });
    } catch {
      setStatus({
        kind: 'error',
        message:
          "We couldn't reach the server. Check your connection and re-run `tizok login`.",
      });
    }
  }

  return (
    <CliAuthFrame title="Authorize Tizok CLI">
      <p>
        The Tizok CLI on your local machine is asking permission to act on
        your behalf. Approving will mint a device-bound token and send it
        back to your terminal — no copying or pasting required.
      </p>

      {me && (
        <p className="cli-auth-identity">
          Signed in as <strong>{me.display_name}</strong>{' '}
          <span className="cli-auth-identity-email">({me.email})</span>
        </p>
      )}

      {status.kind === 'error' && (
        <div className="login-form-error" role="alert">
          {status.message}
        </div>
      )}

      <form onSubmit={handleAuthorize} aria-label="Authorize CLI">
        <div className="cli-auth-actions">
          <button
            type="submit"
            className="login-form-submit"
            disabled={status.kind === 'submitting'}
          >
            {status.kind === 'submitting' && (
              <span className="button-spinner" aria-hidden="true" />
            )}
            {status.kind === 'submitting' ? 'Authorizing…' : 'Authorize'}
          </button>
          <a className="cli-auth-cancel" href="/">
            Cancel
          </a>
        </div>
      </form>

      <p className="cli-auth-hint">
        Token lifetime: 30 days. Revoke with{' '}
        <code>tizok account devices revoke &lt;id&gt;</code> or{' '}
        <code>tizok logout --all</code> if you lose the device.
      </p>
    </CliAuthFrame>
  );
}

interface CliAuthFrameProps {
  title: string;
  children: React.ReactNode;
}

function CliAuthFrame({ title, children }: CliAuthFrameProps) {
  return (
    <div className="cli-auth-page">
      <div className="cli-auth-card">
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}
